import React, { useLayoutEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

/**
 * CRA / webpack-dev-server hata overlay'ini gizler; kullanıcı sadece ErrorBoundary fallback görsün.
 */
function hideDevErrorOverlay() {
  if (typeof document === "undefined") return;
  const selectors = [
    "#webpack-dev-server-client-overlay",
    "#react-error-overlay",
    ".react-error-overlay",
    "[data-react-error-overlay]",
    "iframe[style*='position: fixed'][style*='inset: 0']",
  ];
  selectors.forEach((sel) => {
    try {
      const el = document.querySelector(sel);
      if (el) {
        el.style.setProperty("display", "none", "important");
        el.setAttribute("aria-hidden", "true");
      }
    } catch (_) {}
  });
  const bodyChildren = document.body?.children;
  if (bodyChildren) {
    for (let i = 0; i < bodyChildren.length; i++) {
      const node = bodyChildren[i];
      if (node.id === "root") continue;
      if (node.tagName === "SCRIPT" || node.tagName === "NOSCRIPT") continue;
      const style = node.getAttribute?.("style") || "";
      if (
        node.tagName === "IFRAME" ||
        (style.includes("position: fixed") && style.includes("z-index"))
      ) {
        node.style.setProperty("display", "none", "important");
      }
    }
  }
}

/**
 * React Error Boundary: Render sırasında oluşan hataları yakalar,
 * kullanıcıya teknik detay yerine anlaşılır bir mesaj gösterir.
 * Dev overlay gösterilmez; sadece bu fallback görünür.
 */
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    hideDevErrorOverlay();
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught:", error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <this.props.Fallback
          error={this.state.error}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function ErrorFallback({ error, onRetry }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation("common");

  useLayoutEffect(() => {
    hideDevErrorOverlay();
    const t1 = requestAnimationFrame(() => hideDevErrorOverlay());
    const t2 = setTimeout(hideDevErrorOverlay, 0);
    const t3 = setTimeout(hideDevErrorOverlay, 100);
    return () => {
      cancelAnimationFrame(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        background: isDark
          ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      }}
    >
      <Box
        sx={{
          maxWidth: 460,
          width: "100%",
          textAlign: "center",
          p: { xs: 3.5, sm: 4.5 },
          borderRadius: "20px",
          boxShadow: isDark
            ? "0 24px 48px rgba(0, 0, 0, 0.4)"
            : "0 20px 45px rgba(15, 23, 42, 0.08)",
          backgroundColor: isDark ? "#1e293b" : "#ffffff",
          border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: isDark ? "#f87171" : "#ef4444",
            mb: 2.5,
          }}
        >
          <ErrorOutlineOutlinedIcon sx={{ fontSize: 40 }} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: isDark ? "#f8fafc" : "#0f172a",
            mb: 1,
            fontSize: { xs: "1.25rem", sm: "1.4rem" },
          }}
        >
          {t("errorBoundaryTitle")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: isDark ? "#94a3b8" : "#64748b",
            mb: 3.5,
            fontSize: "0.95rem",
            lineHeight: 1.6,
          }}
        >
          {t("errorBoundaryMessage")}
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<RefreshOutlinedIcon />}
            onClick={handleReload}
            sx={{
              bgcolor: "#2563eb",
              "&:hover": { bgcolor: "#1d4ed8" },
              color: "#ffffff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 1.1,
              fontSize: "0.9rem",
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
            }}
          >
            {t("errorBoundaryReload")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<HomeOutlinedIcon />}
            onClick={handleGoHome}
            sx={{
              borderColor: isDark ? "#475569" : "#cbd5e1",
              bgcolor: isDark ? "transparent" : "#f8fafc",
              color: isDark ? "#e2e8f0" : "#334155",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 1.1,
              fontSize: "0.9rem",
              "&:hover": {
                borderColor: isDark ? "#64748b" : "#94a3b8",
                bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f1f5f9",
              },
            }}
          >
            {t("errorBoundaryGoHome")}
          </Button>
        </Box>
        {onRetry && (
          <Button
            variant="text"
            size="small"
            onClick={onRetry}
            sx={{
              mt: 2.5,
              color: isDark ? "#64748b" : "#94a3b8",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {t("errorBoundaryTryAgain")}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function ErrorBoundary({ children }) {
  return (
    <ErrorBoundaryClass Fallback={ErrorFallback}>
      {children}
    </ErrorBoundaryClass>
  );
}
